import React, {useCallback, useEffect, useMemo, useState} from "react";
import Utils from "../../../../../../../core/services/utils/utils";
import {Box, Button, Card, Checkbox, Chip, DialogContent, DialogTitle, Divider, MenuItem, Paper, Skeleton, TextField, Tooltip, Typography} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import LookupSearchViewSearchAutocomplete from "../search-autocomplete";
import {ReactComponent as Icon} from "../../../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as IconCheckbox} from "../../../../../../../assets/images/check-box/checkbox-active.svg";
import {InfoOutlined} from "@mui/icons-material";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Dialog from "../../../../../../components/base/dialog";
import {ReactComponent as SaveIcon} from "../../../../../../../assets/images/save-rollback/check.svg";
import {ReactComponent as CancelIcon} from "../../../../../../../assets/images/save-rollback/cancel.svg";
import BroadcastService from "../../../../../../../core/services/broadcast";
import {BizLearnVideos} from "../../../../../../components/panel-sections/app-bar/biz-learn-videos-action";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";
import {NexusSearchSchemaEntryNames, ProductTechnicalPropertyTypeIds} from "../../../../../../../core/constants/enums";
import CopyToClipboard from "../../../../../../components/base/copy-to-clipboard";

const initialAdvancedSearchDialogState = {
    open: false,
    onClose: () => void 0,
}

const LookupSearchViewTopbarSearch = ({
                                          searchData,
                                          setSearchData,
                                          loading,
                                          searchSchema,
                                          includeProductListFilters,
                                          searchToken,
                                          advancedSearch,
                                          setAdvancedSearch,
                                          featureFlags,
                                          readonly,
                                          resetOrderByToDefault,
                                      }) => {
    const keywordAppliedFilters = useMemo(() => searchSchema.entries?.filter(e => e.searchable) ?? [], [searchSchema.entries]);
    const [advancedSearchDialogState, setAdvancedSearchDialogState] = useState(initialAdvancedSearchDialogState);
    const {highestVersion: getCachedAdvancedSearch, save: saveCachedAdvancedSearch} = useSavedComponentsState({name: "nexus_search_advanced_search", version: '1.0.0'});
    const isMounted = useIsMounted();

    /**
     * Fetches the cached advanced search data
     * @type {(function(): Promise<void>)}
     */
    const _getCachedAdvancedSearch = useCallback(async () => {
        const cachedAdvancedSearch = await getCachedAdvancedSearch();
        if (!isMounted())
            return;
        if (cachedAdvancedSearch?.data) {
            const advancedSearch = cachedAdvancedSearch.data.value ?? false;
            setAdvancedSearch(advancedSearch);
        }
    }, [isMounted, getCachedAdvancedSearch, setAdvancedSearch]);

    /**
     * As soon as the component mounts,
     * - fetch the cached advanced search data
     */
    useEffect(() => {
        void _getCachedAdvancedSearch();
    }, [_getCachedAdvancedSearch]);

    /**
     * Listens for the advanced search data changes.
     */
    useEffect(() => {
        const {close: bcClose} = BroadcastService.addEventListener(BroadcastService.channelNames.nexusSearchAdvancedSearch, 'message', setAdvancedSearch);
        return () => {
            bcClose();
        }
    }, [setAdvancedSearch]);

    /**
     * Saves the advanced search data
     * @type {(function(*): Promise<void>)}
     */
    const saveAdvancedSearch = useCallback(async (value) => {
        setAdvancedSearch(value);
        BroadcastService.postMessage(BroadcastService.channelNames.nexusSearchAdvancedSearch, value);
        await saveCachedAdvancedSearch({value: value})
    }, [saveCachedAdvancedSearch, setAdvancedSearch])

    /**
     * Opens the advanced search dialog
     * @type {(function(): void)|*}
     */
    const openAdvancedSearchDialog = useCallback(() => {
        setAdvancedSearchDialogState({
            open: true,
            onClose: () => setAdvancedSearchDialogState(initialAdvancedSearchDialogState),
        })
    }, [])


    /**
     * Removes the filter from the search data
     * @type {(function(*): void)}
     */
    const removeFilter = useCallback((filter) => {
        setSearchData(prevState => ({
            ...prevState,
            filters: prevState.filters.filter(e => e.filterId !== filter.filterId || e.value !== filter.value)
        }))
    }, [setSearchData])

    /**
     * Removes the keywords from the search data
     * @type {(function(): void)}
     */
    const removeKeywords = useCallback(() => {
        setSearchData(prevState => ({...prevState, keyword: ''}))
    }, [setSearchData])

    const varietyFilterId = useMemo(() => searchSchema.entries.find(s => s.name === NexusSearchSchemaEntryNames.variety)?.id ?? null, [searchSchema.entries]);

    /**
     * Removes the variance from the search data
     * @type {(function(): void)}
     */
    const removeVariance = useCallback(() => {
        setSearchData(prevState => ({
            ...prevState,
            variance: undefined,
            filters: prevState.filters.filter(e => e.filterId !== varietyFilterId),
        }))
    }, [setSearchData, varietyFilterId])

    /**
     * Removes all the filters from the search data and restores the partner default sort.
     * @type {(function(): void)}
     */
    const removeAllFilters = useCallback(() => {
        setSearchData(prevState => ({...prevState, keyword: '', filters: [], variance: undefined}))
        if (resetOrderByToDefault) {
            resetOrderByToDefault();
        } else {
            setSearchData(prevState => ({...prevState, orderBy: null}))
        }
    }, [setSearchData, resetOrderByToDefault])

    const renderedFilters = useMemo(() => {
        return searchData.filters
            .map(e => {
                const schemaEntry = searchSchema.entries.find(s => s.id === e.filterId);
                if (!schemaEntry)
                    return null
                // Remove the variance filter from the list of filters to be rendered
                if (varietyFilterId && e.filterId === varietyFilterId)
                    return null;
                return {
                    ...e,
                    title: schemaEntry?.title ?? "",
                    displayValue: Utils.formatTechnicalPropertyValueDisplay(schemaEntry.dataType, e.value, schemaEntry.nullReplacement),
                    ...(schemaEntry.dataType === ProductTechnicalPropertyTypeIds.color ? {tooltipValue: e.value} : {}),
                }
            })
            .filter(e => !!e);
    }, [searchData.filters, searchSchema.entries, varietyFilterId])

    /**
     * Opens the tutorial link
     * @type {(function(): void)}
     */
    const openTutorialLink = useCallback(() => {
        window.open(BizLearnVideos.nexusSearch.url, '_blank');
    }, [])

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{
                    // marginBlock: 2,
                    padding: 2,
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Paper sx={{boxShadow: 'none', padding: 2, display: 'flex', flexDirection: 'column', gap: 0}}>
                    <LookupSearchViewSearchAutocomplete
                        searchData={searchData}
                        disabled={readonly}
                        setSearchData={setSearchData}
                        isAdvancedMode={advancedSearch && featureFlags.canToggleAdvancedMode}
                        hideSuggestions={!featureFlags.canSeeAutocompleteSuggestions}
                        loading={loading}
                        searchSchema={searchSchema}
                        searchToken={searchToken}
                        showPartQuickLinks={includeProductListFilters}
                        sx={{marginBlockEnd: includeProductListFilters ? 3 : 0}}
                        endAdornment={
                            featureFlags.canToggleAdvancedMode &&
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: 'secondary.light',
                                gap: 0.5,
                                borderRadius: 1,
                                padding: 0.25,
                                paddingInline: 0.5,
                                marginInline: 1,
                            }}>
                                <Checkbox
                                    size={'small'}
                                    icon={<Icon/>}
                                    checkedIcon={<IconCheckbox/>}
                                    checked={advancedSearch}
                                    sx={{
                                        padding: 0,
                                    }}
                                    onChange={(e) => saveAdvancedSearch(e.target.checked)}
                                />
                                <Typography variant={'caption'}>
                                    Advanced
                                </Typography>
                                <InfoOutlined color={'secondary'} onClick={openAdvancedSearchDialog} sx={{cursor: 'pointer'}}/>
                            </Box>
                        }
                        TextFieldProps={{
                            label: 'Search',
                        }}
                    />
                    {
                        includeProductListFilters &&
                        <>
                            <Grid container spacing={2}>
                                <Grid xs={12} md={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        variant={'outlined'}
                                        size={'small'}
                                        label={'Visibility'}
                                        value={searchData.isVisible?.toString() ?? ' '}
                                        SelectProps={{
                                            renderValue: (value) => value === 'true'
                                                ? 'Visible'
                                                : value === 'false'
                                                    ? 'Invisible'
                                                    : 'All Products'
                                        }}
                                        onChange={(e) => setSearchData(prevState => ({
                                            ...prevState,
                                            isVisible: e.target.value === 'true' ? true : e.target.value === 'false' ? false : null
                                        }))}
                                        disabled={loading || readonly}
                                    >
                                        <MenuItem value={' '}>
                                            All Products
                                        </MenuItem>
                                        <MenuItem value={'true'}>
                                            Visible
                                        </MenuItem>
                                        <MenuItem value={'false'}>
                                            Invisible
                                        </MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid xs={12} md={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        variant={'outlined'}
                                        label={'Share Status'}
                                        size={'small'}
                                        value={searchData.isPublicForBuyers?.toString() ?? " "}
                                        SelectProps={{
                                            renderValue: (value) => value === 'true'
                                                ? 'Public'
                                                : value === 'false'
                                                    ? 'Private'
                                                    : 'All Products',
                                        }}
                                        onChange={(e) => setSearchData(prevState => ({
                                            ...prevState,
                                            isPublicForBuyers: e.target.value === 'true' ? true : e.target.value === 'false' ? false : null
                                        }))}
                                        disabled={loading || readonly}
                                    >
                                        <MenuItem value={' '}>
                                            All Products
                                        </MenuItem>
                                        <MenuItem value={'true'}>
                                            Public
                                        </MenuItem>
                                        <MenuItem value={'false'}>
                                            Private
                                        </MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </>
                    }
                </Paper>
                {
                    (loading || searchData.keyword || searchData.filters.length > 0) &&
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                        {
                            loading &&
                            Array.from({length: 3}).map((_, index) => (
                                <Skeleton
                                    key={`loading-${index}`}
                                    sx={{borderRadius: 2, transform: 'none'}}
                                    variant={'rounded'}
                                    width={120}
                                    height={35}
                                />
                            ))
                        }
                        {
                            !loading &&
                            <>
                                {
                                    (searchData.keyword || searchData.filters.length > 0) &&
                                    <Chip
                                        label={
                                            <Box component={'span'} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                <Typography variant={'caption'}>
                                                    Remove all filters
                                                </Typography>
                                            </Box>
                                        }
                                        onClick={() => removeAllFilters()}
                                        disabled={readonly}
                                        onDelete={() => removeAllFilters()}
                                        color={'warning'}
                                        variant={'solid'}
                                        size={'small'}
                                    />
                                }
                                {
                                    searchData.keyword &&
                                    <CopyToClipboard>
                                        {({copy}) => (
                                            <Tooltip
                                                placement={'bottom'}
                                                title={
                                                    !keywordAppliedFilters.length
                                                        ? ""
                                                        : <Box sx={{display: 'flex', flexDirection: 'column', width: 220}}>
                                                            <Typography variant={'caption'}>
                                                                Keywords applied to the following entities:
                                                            </Typography>
                                                            <Grid container>
                                                                {
                                                                    keywordAppliedFilters.map(e => (
                                                                        <Grid key={e.id} xs={6}>
                                                                            <Typography variant={'caption'} fontWeight={600}>
                                                                                {e.title ?? ""}
                                                                            </Typography>
                                                                        </Grid>
                                                                    ))
                                                                }
                                                            </Grid>
                                                        </Box>
                                                }
                                            >
                                                <Chip
                                                    label={
                                                        <Box component={'span'} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                            <Typography variant={'caption'}>
                                                                Keywords:
                                                            </Typography>
                                                            <Typography variant={'caption'} fontWeight={600} color={'text.primary'}>
                                                                {searchData.keyword}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    onClick={() => copy(searchData.keyword)}
                                                    onDelete={() => removeKeywords()}
                                                    disabled={readonly}
                                                    color={'secondary'}
                                                    variant={'outlined'}
                                                    size={'small'}
                                                    sx={{cursor: 'pointer'}}
                                                />
                                            </Tooltip>
                                        )}
                                    </CopyToClipboard>
                                }
                                {
                                    !!searchData.variance &&
                                    <CopyToClipboard>
                                        {({copy}) => (
                                            <Chip
                                                key={'variance-filter'}
                                                disabled={readonly}
                                                label={
                                                    <Box component={'span'} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                        <Typography variant={'caption'}>
                                                            More options of:
                                                        </Typography>
                                                        <Typography variant={'caption'} fontWeight={600} color={'text.primary'}>
                                                            {searchData.variance?.partNo ?? "Part Number"}
                                                        </Typography>
                                                    </Box>
                                                }
                                                onClick={() => copy(searchData.variance?.partNo ?? '')}
                                                onDelete={() => removeVariance()}
                                                color={'secondary'}
                                                variant={'outlined'}
                                                size={'small'}
                                                sx={{cursor: 'pointer'}}
                                            />
                                        )}
                                    </CopyToClipboard>
                                }
                                {
                                    renderedFilters.map(filter => (
                                        <CopyToClipboard key={filter.key ?? `${filter.title}-${filter.displayValue}`}>
                                            {({copy}) => (
                                                <Chip
                                                    disabled={readonly}
                                                    label={
                                                        <Box component={'span'} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                            <Typography component={'span'} variant={'caption'}>
                                                                {filter.title}:
                                                            </Typography>
                                                            <Typography component={'span'} variant={'caption'} fontWeight={600} color={'text.primary'}>
                                                                {filter.displayValue}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    onClick={() => copy(`${filter.title}: ${filter.displayValue}`)}
                                                    onDelete={() => removeFilter(filter)}
                                                    color={'primary'}
                                                    variant={'outlined'}
                                                    size={'small'}
                                                    sx={{cursor: 'pointer'}}
                                                />
                                            )}
                                        </CopyToClipboard>
                                    ))
                                }
                            </>
                        }
                    </Box>
                }
            </Card>
            <Dialog
                open={advancedSearchDialogState.open}
                setOpen={advancedSearchDialogState.onClose}
                maxWidth={'sm'}
                fullWidth
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Nexus Search Modes
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2 pt-4 pb-3'} sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,}}>
                    <Box sx={{display: 'flex', gap: 1}}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, width: 'calc(50% - 12px)', paddingInline: 1}}>
                            <Typography variant={'subtitle1'} color={!advancedSearch ? 'tertiary.main' : 'text.primary'} fontWeight={600}
                                        width={'100%'} textAlign={'center'}>
                                Basic Mode
                            </Typography>
                            <Typography variant={'caption'} textAlign={'center'} width={'100%'} marginBlockEnd={2}>
                                Enable Basic Search for products using the keywords.
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Keywords Autocomplete Search
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Customized Sorting of the Results
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Most Frequent applicable facets list
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Manual Facets Lookup
                            </Typography>
                            <Typography variant={'caption'} color={'text.secondary'} component={'div'}>
                                <CancelIcon className={'cancel-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Facets Autocomplete Search
                            </Typography>
                        </Box>
                        <Divider orientation={'vertical'} flexItem/>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, width: 'calc(50% - 12px)', paddingInline: 1}}>
                            <Typography variant={'subtitle1'} color={advancedSearch ? 'tertiary.main' : 'text.primary'} fontWeight={600}
                                        width={'100%'} textAlign={'center'}>
                                Advanced Mode
                            </Typography>
                            <Typography variant={'caption'} textAlign={'center'} width={'100%'} marginBlockEnd={2}>
                                Enable advanced search to search for products using multiple search criteria.
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Keywords Autocomplete Search
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Customized Sorting of the Results
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Most Frequent applicable facets list
                            </Typography>
                            <Typography variant={'caption'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Manual Facets Lookup
                            </Typography>
                            <Typography variant={'caption'} color={'secondary'} component={'div'}>
                                <SaveIcon className={'save-icon-new'} style={{display: 'inline-block', marginInlineEnd: 8}}/>
                                Facets Autocomplete Search
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        size={'medium'}
                        variant={'contained'}
                        color={'primary'}
                        onClick={openTutorialLink}
                        fullWidth
                        sx={{marginBlockStart: 4}}
                    >
                        Learn More
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default LookupSearchViewTopbarSearch;
